import React from 'react';
import { Redirect, Route, RouteProps, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppStore, RequestStatus, UserStatus } from '@akademiasaas/shared';
import { Spin } from 'antd';
import { auth } from '~/initializeStore';

const ProtectedRoute = ({ component: Component, ...rest }: RouteProps) => {
  const {
    data: user,
    status,
    detailsStatus,
    details,
  } = useSelector((store: AppStore) => store.user);
  const history = useHistory();
  const continuePath = history.location.pathname + history.location.search;

  if (!Component) {
    throw new Error('Component is missing');
  }
  const authPath = `/auth/login?continue=${encodeURIComponent(continuePath)}`;

  // An anonymous session (public submission page) never gets a user document, so
  // it would spin forever below — treat it as unauthenticated instead. AuthChecker
  // signs the anonymous session out once it lands on /auth.
  if (auth.currentUser?.isAnonymous) {
    return <Redirect to={authPath} />;
  }

  const isLoggingIn =
    status === UserStatus.LOGGING_IN ||
    (detailsStatus === null && status === UserStatus.IS_LOGGED) ||
    (detailsStatus === RequestStatus.SUCCESS &&
      status === UserStatus.IS_LOGGED &&
      details === null) ||
    detailsStatus === RequestStatus.FETCHING;

  if (isLoggingIn) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Soft block: a registered but unconfirmed account stays parked on the verify
  // screen. The Auth record is the only trustworthy source here - an equivalent
  // flag on the user document would be client-writable under firestore.rules.
  if (auth.currentUser && !auth.currentUser.emailVerified) {
    return (
      <Redirect to={`/auth/verify-email?continue=${encodeURIComponent(continuePath)}`} />
    );
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        return user && details ? <Component {...props} /> : <Redirect to={authPath} />;
      }}
    />
  );
};
export default ProtectedRoute;
